import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnInit,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentfulService } from '@core/services/contentful.service';
import { MetadataService } from '@core/services/metadata.service';
import { fixAssetUrl } from '@features/contentful/helpers/assetHelpers';
import { FaqContentfulListComponent } from '@features/faq/faq-contentful-list/faq-contentful-list.component';
import { KnowledgeCategoryListComponent } from '@features/knowledge/knowledge-category-list/knowledge-category-list.component';
import { KnowledgeTopReadsComponent } from '@features/knowledge/knowledge-top-reads/knowledge-top-reads.component';
import { KnowledgeVariationCardsComponent } from '@features/knowledge/knowledge-variation-cards/knowledge-variation-cards.component';
import {
    Configurations,
    CropFocus,
    LazyPictureComponent,
    Stops,
} from '@features/lazy-image/components/lazy-picture/lazy-picture.component';
import { NavigationComponent } from '@features/navigation/components/navigation/navigation.component';
import { KnowledgeArticle, KnowledgeDestination } from '@generated/graphql';
import { CtaButtonComponent } from '@shared/cta-button/cta-button.component';
import { NavigationScrollInverseColorsDirective } from '@shared/navigation-scroll-inverse-colors/navigation-scroll-inverse-colors.directive';
import { SwirlComponent } from '@shared/swirl/swirl.component';
import { Observable, combineLatest } from 'rxjs';
import { catchError, map, pluck, shareReplay } from 'rxjs/operators';

@Component({
    selector: 'app-knowledge-destination-detail-page',
    templateUrl: './knowledge-destination-detail-page.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NavigationComponent,
        NavigationScrollInverseColorsDirective,
        LazyPictureComponent,
        SwirlComponent,
        KnowledgeTopReadsComponent,
        KnowledgeCategoryListComponent,
        KnowledgeVariationCardsComponent,
        FaqContentfulListComponent,
        AsyncPipe,
        CtaButtonComponent,
    ],
})
export class KnowledgeDestinationDetailPageComponent implements OnInit {
    @ViewChild('allTopics') element: ElementRef;

    destination: Observable<KnowledgeDestination>;
    topReads: Observable<KnowledgeArticle[]>;
    featuredImageConfiguration: Observable<Configurations>;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private contentfulService: ContentfulService,
        private metadataService: MetadataService
    ) {}

    ngOnInit(): void {
        const destinationParam = this.activatedRoute.params.pipe(
            pluck('destination')
        );
        const mappedDestinationsBySlug = this.contentfulService
            .destinations()
            .pipe(
                map(destinations =>
                    Object.assign(
                        {},
                        ...destinations.data.knowledgeDestinationCollection?.items.map(
                            k => ({ [k.urlSlug]: k })
                        )
                    )
                )
            );
        this.destination = combineLatest([
            destinationParam,
            mappedDestinationsBySlug,
        ]).pipe(
            map(([params, destinations]) => {
                if (!(params in destinations)) {
                    throw new Error('Invalid url slug');
                } else {
                    return destinations[params];
                }
            }),
            shareReplay(1),
            catchError(err => {
                console.error(err);
                this.router.navigate(['/knowledge']);
                return null;
            })
        );
        this.topReads = this.destination.pipe(
            map(destination => destination.topReadsCollection?.items || []),
            shareReplay(1)
        );

        // Adding Meta Tags from contentful Data
        this.destination.subscribe(data => {
            if (data.seoTitle) {
                this.metadataService.setTitle(data.seoTitle);
            } else {
                this.metadataService.setTitle(data.name);
            }
            this.metadataService.setAttribute({
                name: 'description',
                content: data.seoDescription || data.description,
            });
            this.metadataService.setAttribute({
                property: 'og:description',
                content: data.seoDescription || data.description,
            });
            this.metadataService.setAttribute({
                property: 'og:image',
                content: `${fixAssetUrl(
                    data.featuredImageWide.url
                )}?w=1200&h=630&f=${
                    data.featuredImageCropFocus
                }&fit=fill&fm=jpg`,
            });
            this.metadataService.setAttribute({
                property: 'og:type',
                content: 'website',
            });
            this.metadataService.setCanonicalUrl(this.router.url);
        });

        this.featuredImageConfiguration = this.destination.pipe(
            map(destination => ({
                [Stops.FALLBACK]: {
                    image: destination.featuredImageSquare,
                    ratioHeight: 3,
                    ratioWidth: 2,
                    width: 1200,
                    width2x: 1200,
                    focus:
                        (destination.featuredImageCropFocus as CropFocus) ||
                        'top',
                },
                [Stops.BASE]: {
                    image: destination.featuredImageSquare,
                    ratioHeight: 3,
                    ratioWidth: 2,
                    width: 400,
                    width2x: 600,
                    focus:
                        (destination.featuredImageCropFocus as CropFocus) ||
                        'top',
                },
                [Stops.MD]: {
                    image: destination.featuredImageWide,
                    ratioHeight: 1.2,
                    ratioWidth: 2,
                    width: 800,
                    width2x: 1600,
                    focus:
                        (destination.featuredImageCropFocus as CropFocus) ||
                        'top',
                },
                [Stops.LG]: {
                    image: destination.featuredImageWide,
                    ratioHeight: 1.2,
                    ratioWidth: 2,
                    width: 1024,
                    width2x: 1600,
                    focus:
                        (destination.featuredImageCropFocus as CropFocus) ||
                        'top',
                },
                [Stops.XL]: {
                    image: destination.featuredImageWide,
                    ratioHeight: 1.2,
                    ratioWidth: 3,
                    width: 1200,
                    width2x: 1600,
                    focus:
                        (destination.featuredImageCropFocus as CropFocus) ||
                        'top',
                },
                [Stops.XXL]: {
                    image: destination.featuredImageWide,
                    ratioHeight: 1.2,
                    ratioWidth: 4,
                    width: 1600,
                    width2x: 1600,
                    focus:
                        (destination.featuredImageCropFocus as CropFocus) ||
                        'top',
                },
            }))
        );
    }

    scroll() {
        this.element.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }
}
